import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabaseClient';

const { FiBriefcase, FiCheckCircle, FiCircle, FiMapPin, FiUser, FiInfo, FiLoader } = FiIcons;

export default function PackedBagPublicView() {
  const { id } = useParams();
  const [bag, setBag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBag();
  }, [id]);

  const fetchBag = async () => {
    try {
      const { data: bagData, error: bagError } = await supabase
        .from('packed_bags')
        .select(`
          *,
          child:family_members(name, color, avatar),
          items:packed_bag_items(*)
        `)
        .eq('id', id)
        .single();

      if (bagError) throw bagError;
      setBag(bagData);
    } catch (err) {
      setError("Tasche nicht gefunden oder Link ist abgelaufen.");
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = async (itemId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('packed_bag_items')
        .update({ is_returned: !currentStatus })
        .eq('id', itemId);

      if (error) throw error;
      
      setBag(prev => ({
        ...prev,
        items: prev.items.map(i => i.id === itemId ? { ...i, is_returned: !currentStatus } : i)
      }));
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
      <SafeIcon icon={FiLoader} className="w-10 h-10 text-blue-600 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-950 text-center">
      <div className="max-w-md">
        <div className="bg-red-100 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
          <SafeIcon icon={FiBriefcase} className="w-10 h-10 text-red-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Hoppla!</h1>
        <p className="text-gray-600 dark:text-gray-400">{error}</p>
      </div>
    </div>
  );

  const returnedCount = bag.items?.filter(i => i.is_returned).length || 0;
  const totalCount = bag.items?.length || 0;
  const isComplete = totalCount > 0 && returnedCount === totalCount;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-12">
      {/* Header */}
      <div className="bg-blue-600 text-white pt-12 pb-24 px-6 rounded-b-[3rem] shadow-xl">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center space-x-4 mb-6">
            <div 
              className="w-16 h-16 rounded-3xl flex items-center justify-center text-white font-bold text-2xl shadow-lg border-2 border-white/20"
              style={{ backgroundColor: bag.child?.color || '#3B82F6' }}
            >
              {bag.child?.avatar || (bag.child?.name ? bag.child.name.charAt(0) : '?')}
            </div>
            <div>
              <h1 className="text-2xl font-bold">Packliste für {bag.child?.name}</h1>
              <p className="opacity-90 flex items-center">
                <SafeIcon icon={FiMapPin} className="mr-1 w-4 h-4" />
                Ziel: {bag.destination}
              </p>
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Bereit für die Rückreise</span>
              <span className="text-sm font-bold">{returnedCount} / {totalCount}</span>
            </div>
            <div className="w-full bg-white/20 h-2 rounded-full overflow-hidden">
              <motion.div 
                animate={{ width: `${(returnedCount / totalCount) * 100}%` }}
                className="h-full bg-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-lg mx-auto -mt-12 px-6">
        <div className="bg-white dark:bg-gray-900 rounded-[2.5rem] shadow-xl p-8 border border-gray-100 dark:border-gray-800">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center">
            <SafeIcon icon={FiBriefcase} className="mr-2 text-blue-600" />
            Tascheninhalt prüfen
          </h2>

          <div className="space-y-3">
            {bag.items.map(item => (
              <button
                key={item.id}
                onClick={() => toggleItem(item.id, item.is_returned)}
                className={`w-full flex items-center p-4 rounded-2xl border transition-all ${
                  item.is_returned 
                    ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' 
                    : 'bg-gray-50 border-gray-100 dark:bg-gray-800 dark:border-gray-700'
                }`}
              >
                <div className={`mr-4 transition-colors ${item.is_returned ? 'text-green-600' : 'text-gray-400'}`}>
                  <SafeIcon icon={item.is_returned ? FiCheckCircle : FiCircle} className="w-6 h-6" />
                </div>
                <span className={`flex-1 text-left font-medium ${item.is_returned ? 'text-green-800 dark:text-green-200 line-through opacity-70' : 'text-gray-700 dark:text-gray-200'}`}>
                  {item.name}
                </span>
              </button>
            ))}
          </div>

          {bag.notes && (
            <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800">
              <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-1 flex items-center">
                <SafeIcon icon={FiInfo} className="mr-1 w-3 h-3" /> Wichtige Notizen
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300">{bag.notes}</p>
            </div>
          )}

          {isComplete && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 p-4 bg-green-500 text-white rounded-2xl text-center font-bold shadow-lg shadow-green-500/30"
            >
              Alles eingepackt! Gute Heimreise! 🏠
            </motion.div>
          )}
        </div>
        
        <p className="text-center mt-8 text-xs text-gray-400">
          Powered by FamilyManager - Tasche gepackt von {bag.child?.name}'s Familie
        </p>
      </div>
    </div>
  );
}